/**
 * 设计 token —— 视觉一致性的唯一来源。
 *
 * 此前全站散落 47 种硬编码十六进制色值，光中性灰就有 #52525B / #71717A /
 * #A1A1AA / #8A8A99 四档随意混用；字号跨 12 档（7px 到 2xl）；圆角 6 种。
 * 这种"每处各写各的"正是画面显得廉价的直接原因 —— 元素之间缺少可感知的秩序。
 *
 * 约定：新增 UI 一律引用这里的 token，不要再写字面量色值/尺寸。
 */

/** 品牌色：深空黑底 + 电光蓝主色 + 紫罗兰 + 青色高光（MC ALLIANCE 主视觉） */
export const brand = {
  blue: '#2E6BFF',
  blueLight: '#5B8CFF',
  blueSoft: '#93B4FF',
  blueDeep: '#1E4ACC',
  violet: '#7B3FE4',
  cyan: '#22D3EE',
  green: '#4CAF8A',
  red: '#E85D5D',
} as const

/** 背景层级：越往上层越亮，用层次代替生硬的边框来区分区块 */
export const surface = {
  /** 页面底色 */
  base: '#050510',
  /** 一级卡片 */
  raised: '#0C0C1C',
  /** 二级（卡片内的分区） */
  overlay: '#12122A',
  /** 分隔线 / 描边 */
  line: 'rgba(255,255,255,0.07)',
  /** 卡片内的凹陷区（数据格） */
  sunken: 'rgba(0,0,0,0.25)',
} as const

/**
 * 文本层级：只保留四档，强制拉开对比。
 * 原先四种灰值混用导致主次不分 —— 次要信息和正文一样重，画面就"平"。
 */
export const text = {
  /** 标题、关键数字 */
  primary: '#FFFFFF',
  /** 正文 */
  secondary: '#A1A1AA',
  /** 辅助说明 */
  tertiary: '#71717A',
  /** 标签、单位等最弱信息 */
  muted: '#52525B',
} as const

/**
 * 字号：收敛为 7 档语义化尺寸。
 * 原先 12 档（含 7px / 8px / 9px）过于碎，相邻档差异小到无法形成层级。
 */
export const fontSize = {
  /** 单位、角标 */
  micro: 10,
  /** 辅助说明 */
  caption: 11,
  /** 正文 */
  body: 13,
  /** 小标题 */
  subtitle: 15,
  /** 卡片标题 */
  title: 17,
  /** 页面主标题 */
  heading: 20,
  /** 关键数字 */
  display: 26,
} as const

/** 圆角：三档就够。大面积卡片用 lg，内部元素用 md，胶囊用 full */
export const radius = {
  md: 12,
  lg: 16,
  xl: 20,
  full: 9999,
} as const

/** 间距：4 的倍数，保证垂直节奏统一 */
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const

/**
 * 阴影与光晕：品质感主要来自这里。
 * 纯色边框 + 无阴影会让卡片像"贴纸"；加一层同色系的柔光能产生纵深。
 */
export const elevation = {
  /** 卡片默认：极淡的外阴影，仅用于与背景分离 */
  card: '0 1px 2px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.24)',
  /** 强调卡片：带品牌色辉光 */
  glow: (color: string, strength = 0.18) =>
    `0 1px 2px rgba(0,0,0,0.4), 0 12px 32px rgba(0,0,0,0.28), 0 0 40px ${withAlpha(color, strength)}`,
  /** 按钮按下 */
  pressed: 'inset 0 1px 3px rgba(0,0,0,0.5)',
} as const

/** 十六进制色 + 透明度 → rgba，避免到处写 `${color}33` 这类隐晦写法 */
export function withAlpha(hex: string, alpha: number): string {
  const h = hex.replace('#', '')
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * 卡片通用样式。variant 决定强调程度：
 * - plain    普通信息卡
 * - accent   带品牌色辉光的强调卡（如节点档位）
 * - muted    弱化卡（如已售罄）
 */
export function cardStyle(
  variant: 'plain' | 'accent' | 'muted' = 'plain',
  accent: string = brand.blue
): React.CSSProperties {
  if (variant === 'accent') {
    return {
      background: `linear-gradient(155deg, ${withAlpha(accent, 0.1)} 0%, ${surface.raised} 58%)`,
      border: `1px solid ${withAlpha(accent, 0.28)}`,
      borderRadius: radius.lg,
      boxShadow: elevation.glow(accent),
    }
  }
  if (variant === 'muted') {
    return {
      background: surface.raised,
      border: `1px solid ${surface.line}`,
      borderRadius: radius.lg,
      opacity: 0.55,
    }
  }
  return {
    background: surface.raised,
    border: `1px solid ${surface.line}`,
    borderRadius: radius.lg,
    boxShadow: elevation.card,
  }
}
